import React from 'react';

import type { CtaButtonProps } from 'components/global/CtaButton';
import { CtaButton } from 'components/global/CtaButton';
import { LOCAL_COLORS } from 'styles/constants';

import { FaqAccordion } from './components/FaqAccordion';
import { FaqSchema } from './components/FaqSchema';
import {
  bodyStyles,
  desktopButtonStyles,
  FaqInnerBox,
  FaqContainer,
  mobileButtonStyles,
  QuestionsBox,
  FaqCopyAndCtaContainer,
  FaqTitle,
} from './Faq.style';

export interface FaqType {
  question: string;
  richTextQuestion?: React.ReactNode;
  answer: React.ReactNode;
  disclaimer?: string;
}

export interface FaqProps {
  /** 50 characters max, preferred ~30 */
  header: string;
  /** 180 characters max, preferred ~100 */
  body?: React.ReactNode;
  /** 3 faqs is the minimum, 6 is the maximum. Faqs have a question and answer. */
  faqs: Array<FaqType>;
  /** Button text is required. Disclaimer text is optional */
  ctaButton?: CtaButtonProps;
  /** Use to optionally hide the cta button on mobile */
  hideMobileCtaButton?: boolean;
  backgroundColor?: string;
  theme?: string;
}

const moduleName = 'faq';

export const Faq = ({
  body,
  backgroundColor = LOCAL_COLORS.surfaceWhite,
  ctaButton,
  faqs,
  header,
  hideMobileCtaButton,
  theme = 'primary',
}: FaqProps) => {
  const formattedBody =
    typeof body === 'string' ? (
      <p css={bodyStyles} dangerouslySetInnerHTML={{ __html: `${body}` }} />
    ) : (
      <div css={bodyStyles}>{body}</div>
    );

  return (
    <React.Fragment>
      <FaqSchema faqs={faqs} />
      <FaqContainer backgroundColor={backgroundColor} data-module={moduleName}>
        <FaqInnerBox
          hideMobileCtaButton={hideMobileCtaButton}
          themeType={theme}
        >
          <FaqCopyAndCtaContainer themeType={theme}>
            <FaqTitle>{header}</FaqTitle>
            {body && formattedBody}
            {ctaButton && (
              <CtaButton
                {...ctaButton}
                css={desktopButtonStyles}
                module={moduleName}
              />
            )}
          </FaqCopyAndCtaContainer>
          <QuestionsBox themeType={theme}>
            {faqs.map((faq) => (
              <FaqAccordion faq={faq} key={faq.question} themeType={theme} />
            ))}
          </QuestionsBox>
          {!hideMobileCtaButton && ctaButton && (
            <CtaButton
              {...ctaButton}
              css={mobileButtonStyles}
              module={moduleName}
            />
          )}
        </FaqInnerBox>
      </FaqContainer>
    </React.Fragment>
  );
};
